import * as React from "react";
import { createPortal } from "react-dom";
import { usePrevious } from "src/hooks";
import { cn } from "src/utilities/shadcnUtils";

export interface DrawerModalProps {
  children?: React.ReactNode;
  className?: string;
  mounted?: boolean;
  transitionDuration?: number; // Duration in seconds (Default: 1s)
  variant?: "full" | "left" | "right";
}

const VARIANT_CLASSES: Record<"full" | "left" | "right", string> = {
  full: "right-0 left-0 -translate-x-[120%]",
  left: "left-0 -translate-x-[120%]",
  right: "right-0 translate-x-[120%]",
};

const DrawerModal = ({
  children,
  mounted,
  className,
  transitionDuration = 1,
  variant = "full",
}: DrawerModalProps) => {
  const [last, setLast] = React.useState(children);
  const prevChildren = usePrevious(children);
  const prevMounted = usePrevious(mounted);

  React.useEffect(() => {
    // if beginning to unmount, save the children so that we show it on the way down
    // (actually save the children previously rendered, because if we
    // are unmounting, it's probably too late for the actual children)
    if (!mounted) {
      // default to the children if this is the first render
      setLast(prevChildren || children);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, setLast]);

  const onTransitionEnd = React.useCallback(() => {
    if (!mounted) {
      // once we have fully unmounted, don't keep the saved screen
      setLast(children);
    }
  }, [children, mounted, setLast]);

  const el = document.getElementById("root-layout");
  if (!el) {
    return null;
  }

  return createPortal(
    <div
      onTransitionEnd={onTransitionEnd}
      className={cn(
        "fixed top-0 bottom-0 pointer-events-auto z-[1] flex max-w-full ease-in-out",
        VARIANT_CLASSES[variant],
        mounted && "translate-x-0",
        className
      )}
      style={{ transitionDuration: `${transitionDuration}s`, transitionProperty: "transform" }}
    >
      {mounted ? children : prevMounted ? prevChildren : last}
    </div>,
    el
  );
};

export default React.memo(DrawerModal);
